import { hasQuotationBeenEmailed, hasRealProformaNumber, isQuotationIssued, isQuotationSuperseded, isQuotationUsable, lineGross } from '@jira-clone/shared-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import {
  X, Printer, Save, CheckCircle2, XCircle, Send, History, FileText, Edit, Lock, Mail
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { lockHtmlTemplate } from '@/utils/sales/quotationTemplateTokens';

export interface DocumentWorkspaceData {
  id: string;
  number?: string;
  documentNumber?: string;
  clientName?: string;
  customerName?: string;
  clientEmail?: string;
  status: string;
  requestType?: 'quotation' | 'proforma' | 'invoice';
  documentType?: string;
  htmlBody?: string;
  currentHtmlBody?: string;
  subtotal?: number;
  subTotal?: number;
  taxTotal?: number;
  total?: number;
  grandTotal?: number;
  notes?: string;
  reviewNote?: string;
  paymentTerms?: string;
  customerAddress?: string;
  customerEmail?: string;
  customerPhone?: string;
  items?: any[];
  lines?: any[];
  history?: Array<{
    at?: string;
    byName?: string;
    from?: string;
    to?: string;
    note?: string;
  }>;
  documentRevisions?: Array<{
    revisionNumber: number;
    createdByName?: string;
    createdAt?: string;
    note?: string;
    htmlBody: string;
    rejectionNote?: string;
    rejectedBy?: string;
    rejectedAt?: string;
  }>;
  canApproveNow?: boolean;
  canRejectNow?: boolean;
  canSubmit?: boolean;
  canResubmit?: boolean;
  canResubmitProforma?: boolean;
  canRequestTaxInvoiceApproval?: boolean;
  isConfirmed?: boolean;
  isLocked?: boolean;
  userRole?: string;
  // Resubmission-specific fields
  rejectionReason?: string;
  quotationNumber?: string;
  proformaId?: string | null;
  /** Set when the quotation was emailed to the client; after that it is revised, not edited. */
  emailSentAt?: string | null;
  /** Set once a revised quotation has replaced this one. */
  supersededById?: string | null;
  supersededByNumber?: string | null;
  proformaNumber?: string;
  currentRevisionLabel?: string;
  nextRevisionLabel?: string;
  rawRequest?: any;
}

interface SalesDocumentWorkspaceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentWorkspaceData | null;
  readOnly?: boolean;
  onSaveHtml?: (id: string, html: string, note?: string, structuredData?: any) => Promise<void>;
  onApprove?: (id: string, note?: string) => Promise<void>;
  onReject?: (id: string, note: string) => Promise<void>;
  onResubmit?: (id: string, html: string, note?: string) => Promise<void>;
  onRequestTaxInvoiceApproval?: (id: string, html?: string, note?: string) => Promise<void>;
  onOpenResubmissionForm?: (docData: DocumentWorkspaceData) => void;
  onReviseQuotation?: (docData: DocumentWorkspaceData) => void;
  /** Email a confirmed quotation to the client (shown for a quotation that is not a draft or replaced). */
  onSendEmail?: (docData: DocumentWorkspaceData) => void;
  onRequestProforma?: (docData: DocumentWorkspaceData) => void;
  onEditDraft?: (docData: DocumentWorkspaceData) => void;
  onSubmitDraft?: (id: string) => Promise<void>;
}



/** Extract edited HTML from a sandboxed iframe's contentDocument */
function getIframeHtml(iframe: HTMLIFrameElement | null): string | null {
  if (!iframe || !iframe.contentDocument) return null;
  try {
    const doc = iframe.contentDocument;
    // Persist input values as HTML attributes so they survive serialization
    doc.querySelectorAll('input').forEach((inp: HTMLInputElement) => {
      if (inp.type === 'checkbox' || inp.type === 'radio') {
        if (inp.checked) inp.setAttribute('checked', 'checked');
        else inp.removeAttribute('checked');
      } else {
        inp.setAttribute('value', inp.value);
      }
    });
    doc.querySelectorAll('textarea').forEach((ta: HTMLTextAreaElement) => {
      ta.textContent = ta.value;
    });
    doc.querySelectorAll('select').forEach((sel: HTMLSelectElement) => {
      Array.from(sel.options).forEach((opt: HTMLOptionElement) => {
        if (opt.selected) opt.setAttribute('selected', 'selected');
        else opt.removeAttribute('selected');
      });
    });
    // Remove the edit overlay style before saving
    const editStyle = doc.getElementById('_edit_overlay');
    if (editStyle) editStyle.remove();
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  } catch {
    return null;
  }
}

/** Extract structured line items, terms, notes, and totals from iframe DOM */
export function extractStructuredDataFromIframe(iframe: HTMLIFrameElement | null): any {
  if (!iframe || !iframe.contentDocument) return null;
  try {
    const doc = iframe.contentDocument;
    const items: any[] = [];
    const rows = doc.querySelectorAll('tr');

    rows.forEach((tr) => {
      const nameEl = tr.querySelector('.it-name');
      if (!nameEl) return;
      const name = nameEl.textContent?.trim() || '';
      if (!name) return;

      const descEl = tr.querySelector('.it-desc');
      const subDescription = descEl ? descEl.textContent?.trim() : '';

      const hsnEl = tr.querySelector('.hsncol');
      let hsn = hsnEl ? hsnEl.textContent?.trim() : '';

      const parseNum = (str: string | null | undefined): number => {
        if (!str) return 0;
        const cleaned = str.replace(/[^0-9.-]/g, '');
        const val = parseFloat(cleaned);
        return isNaN(val) ? 0 : val;
      };

      const tds = Array.from(tr.querySelectorAll('td'));
      let qty = 1, unit = 'Nos', rate = 0, disc = 0, gst = 18;

      if (tds.length >= 10) {
        hsn = tds[2]?.textContent?.trim() || hsn;
        qty = parseNum(tds[3]?.textContent) || 1;
        unit = tds[4]?.textContent?.trim() || 'Nos';
        rate = parseNum(tds[5]?.textContent) || 0;
        disc = parseNum(tds[6]?.textContent) || 0;
        gst = parseNum(tds[8]?.textContent) || 18;
      } else {
        const nCells = tr.querySelectorAll('td.n');
        if (nCells.length >= 5) {
          qty = parseNum(nCells[0]?.textContent) || 1;
          rate = parseNum(nCells[1]?.textContent) || 0;
          disc = parseNum(nCells[2]?.textContent) || 0;
          gst = parseNum(nCells[4]?.textContent) || 18;
        } else if (tds.length >= 7) {
          qty = parseNum(tds[3]?.textContent) || 1;
          unit = tds[4]?.textContent?.trim() || 'Nos';
          rate = parseNum(tds[5]?.textContent) || 0;
          disc = parseNum(tds[6]?.textContent) || 0;
          if (tds.length >= 9) gst = parseNum(tds[8]?.textContent) || 18;
        }
      }

      const unitEl = tr.querySelector('td.c:not(.hsncol)');
      if (unitEl && !unitEl.querySelector('button') && unit === 'Nos') {
        const uText = unitEl.textContent?.trim();
        if (uText && !/^\d+$/.test(uText)) unit = uText;
      }

      const lineGrossValue = lineGross(qty, rate, unit);
      const lineDisc = lineGrossValue * (disc / 100);
      const taxableValue = Math.round((lineGrossValue - lineDisc) * 100) / 100;
      const taxAmount = Math.round((taxableValue * (gst / 100)) * 100) / 100;
      const lineTotal = Math.round((taxableValue + taxAmount) * 100) / 100;

      items.push({
        name,
        description: name,
        subDescription,
        hsnSac: hsn,
        hsn,
        quantity: qty,
        qty,
        unit,
        rate,
        unitPrice: rate,
        discountPercent: disc,
        discount: disc,
        disc,
        gstRate: gst,
        gst,
        taxRate: gst,
        taxableValue,
        taxAmount,
        lineTotal,
        total: lineTotal,
      });
    });

    // Extract Terms
    let termsStr = '';
    const termsEl = doc.getElementById('terms') || doc.querySelector('.terms-section');
    if (termsEl) {
      const lis = termsEl.querySelectorAll('li');
      if (lis.length > 0) {
        termsStr = Array.from(lis).map(li => li.textContent?.trim()).filter(Boolean).join('\n');
      } else {
        termsStr = termsEl.textContent?.trim() || '';
      }
    }

    // Extract Notes
    let notesStr = '';
    const notesEl = doc.getElementById('notes') || doc.querySelector('.notes-section');
    if (notesEl) {
      const lis = notesEl.querySelectorAll('li');
      if (lis.length > 0) {
        notesStr = Array.from(lis).map(li => li.textContent?.trim()).filter(Boolean).join('\n');
      } else {
        notesStr = notesEl.textContent?.trim() || '';
      }
    }

    // Extract Assumptions
    let assumptionsStr = '';
    const assumptionsEl = doc.getElementById('assumptions');
    if (assumptionsEl) {
      const lis = assumptionsEl.querySelectorAll('li');
      if (lis.length > 0) {
        assumptionsStr = Array.from(lis).map(li => li.textContent?.trim()).filter(Boolean).join('\n');
      } else {
        assumptionsStr = assumptionsEl.textContent?.trim() || '';
      }
    }

    // Extract Milestones if present in table
    const milestones: any[] = [];
    const schedRows = doc.querySelectorAll('#schedBody tr, .msched tbody tr');
    schedRows.forEach((tr, idx) => {
      const cells = tr.querySelectorAll('td');
      if (cells.length >= 4) {
        const name = cells[1]?.textContent?.trim() || `Stage ${idx + 1}`;
        const parseCellNum = (str: string | null | undefined): number => {
          if (!str) return 0;
          const cleaned = str.replace(/[^0-9.-]/g, '');
          const val = parseFloat(cleaned);
          return isNaN(val) ? 0 : val;
        };
        const pct = parseCellNum(cells[2]?.textContent) || 0;
        const taxable = parseCellNum(cells[3]?.textContent) || 0;
        const total = parseCellNum(cells[cells.length >= 9 ? 7 : cells.length - 2]?.textContent) || 0;
        const due = cells[cells.length - 1]?.textContent?.trim() || '—';
        milestones.push({
          milestoneNumber: idx + 1,
          name,
          percentage: pct,
          percent: pct,
          triggerType: idx === 0 ? 'advance' : (idx === schedRows.length - 1 ? 'final_delivery' : 'project_phase'),
          status: 'pending',
          netAmount: taxable,
          milestoneTaxableAmount: taxable,
          amountDue: total,
          amount: total,
          milestoneTotal: total,
          due,
        });
      }
    });

    let subtotal = 0;
    let taxTotal = 0;
    items.forEach((it) => {
      subtotal += it.taxableValue || 0;
      taxTotal += it.taxAmount || 0;
    });
    subtotal = Math.round(subtotal * 100) / 100;
    taxTotal = Math.round(taxTotal * 100) / 100;
    let total = Math.round((subtotal + taxTotal) * 100) / 100;

    // Check rendered totals from summary block if present in DOM
    const parseCurrency = (str: string | null | undefined): number => {
      if (!str) return 0;
      const cleaned = str.replace(/[^0-9.-]/g, '');
      const val = parseFloat(cleaned);
      return isNaN(val) ? 0 : val;
    };

    let domSubtotal = 0;
    let domGrandTotal = 0;
    const summaryRows = doc.querySelectorAll('.totals-block .r, .totals .r, .summary .r, table.totals tr, #sheet .r');
    summaryRows.forEach((row) => {
      const text = row.textContent?.toLowerCase() || '';
      const valEl = row.querySelector('span:last-child, td:last-child');
      const val = parseCurrency(valEl?.textContent);
      if (text.includes('taxable value') && val > 0) {
        domSubtotal = val;
      } else if ((text.includes('grand total') || text.includes('total payable')) && val > 0) {
        domGrandTotal = val;
      }
    });

    if (domSubtotal > 0) subtotal = domSubtotal;
    if (domGrandTotal > 0) total = domGrandTotal;
    if (domGrandTotal > 0 && domSubtotal > 0) taxTotal = Math.max(0, Math.round((domGrandTotal - domSubtotal) * 100) / 100);

    const result: any = {};
    if (items.length > 0) result.items = items;
    if (milestones.length > 0) {
      result.milestones = milestones;
      result.paymentSchedule = {
        paymentType: 'milestone_based',
        totalAmount: total,
        subtotal,
        taxTotal,
        milestones,
      };
    }
    if (termsStr) result.terms = termsStr;
    if (notesStr) result.notes = notesStr;
    if (assumptionsStr) result.assumptions = assumptionsStr;
    if (items.length > 0 || domGrandTotal > 0) {
      result.subtotal = subtotal;
      result.taxTotal = taxTotal;
      result.total = total;
    }

    return result;
  } catch (err) {
    console.error('Failed to extract structured data from iframe:', err);
    return null;
  }
}

export function SalesDocumentWorkspaceDrawer({
  isOpen,
  onClose,
  document,
  readOnly,
  onSaveHtml,
  onApprove,
  onReject,
  onResubmit,
  onRequestTaxInvoiceApproval,
  onOpenResubmissionForm,
  onReviseQuotation,
  onSendEmail,
  onRequestProforma,
  onEditDraft,
  onSubmitDraft,
}: SalesDocumentWorkspaceDrawerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [activeTab, setActiveTab] = useState<'editor' | 'history'>('editor');
  const [isSaving, setIsSaving] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [iframeReady, setIframeReady] = useState(false);

  const docNumber = document?.number || document?.documentNumber || 'DOC-001';
  const client = document?.clientName || document?.customerName || 'Client';
  const html = document?.currentHtmlBody || document?.htmlBody || '';

  const isConfirmed = Boolean(
    document?.isConfirmed ||
    (document as any)?.isLocked ||
    document?.status === 'confirmed' ||
    // an approved proforma is final
    ((document?.requestType === 'proforma' || document?.documentType === 'proforma') && document?.status === 'proforma_approved')
  );

  const isReadOnly = readOnly || isConfirmed;

  // Reset iframe ready state when document changes
  useEffect(() => {
    setIframeReady(false);
  }, [document?.id]);

  const handleIframeLoad = useCallback(() => {
    setIframeReady(true);
    if ((isReadOnly || isConfirmed) && iframeRef.current?.contentDocument) {
      try {
        const doc = iframeRef.current.contentDocument;
        doc.querySelectorAll('[contenteditable]').forEach(el => {
          el.removeAttribute('contenteditable');
        });
        doc.querySelectorAll('.rowdel, .btn-add, .row-actions').forEach(el => {
          el.remove();
        });
      } catch (e) {
        console.warn('Could not strip editable attributes from iframe:', e);
      }
    }
  }, [isReadOnly, isConfirmed]);

  const getEditedHtml = (): string => {
    return getIframeHtml(iframeRef.current) || html;
  };

  const handleSaveDraft = async () => {
    if (!onSaveHtml || !document || isConfirmed) return;
    setIsSaving(true);
    try {
      const currentHtml = getEditedHtml();
      const structuredData = extractStructuredDataFromIframe(iframeRef.current);
      await onSaveHtml(document.id, currentHtml, 'Saved document from drawer', structuredData);
      toast.success('Document saved to database successfully');
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'save this document'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveAction = async () => {
    if (!onApprove || !document) return;
    setIsSaving(true);
    try {
      await onApprove(document.id, 'Approved from document drawer');
      toast.success('Document approved successfully');
      onClose();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'approve this document'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!onReject || !document) return;
    if (!rejectNote.trim()) {
      toast.error('Please enter a reason/note for returning');
      return;
    }
    setIsSaving(true);
    try {
      await onReject(document.id, rejectNote);
      toast.success('Returned to Sales with notes');
      setRejectModalOpen(false);
      onClose();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'return this document'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestTaxInvoiceApprovalAction = async () => {
    if (!onRequestTaxInvoiceApproval || !document) return;
    setIsSaving(true);
    try {
      const currentHtml = getEditedHtml();
      await onRequestTaxInvoiceApproval(document.id, currentHtml, 'Tax invoice approval requested');
      onClose();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'request tax invoice approval'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrintPdf = () => {
    if (iframeRef.current?.contentWindow) {
      try {
        const win = iframeRef.current.contentWindow as any;
        win.focus();
        if (typeof win.printDoc === 'function') {
          win.printDoc();
          return;
        }
        win.print();
        return;
      } catch (err) {
        console.warn('Iframe print failed, falling back to window print', err);
      }
    }
    const currentHtml = getEditedHtml();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(currentHtml);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);
    }
  };


  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-xs flex justify-end">
      <div className="w-full max-w-[95vw] xl:max-w-7xl bg-white dark:bg-card h-full flex flex-col shadow-2xl transition-all border-l border-border">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-border px-4 sm:px-6 py-3 sm:py-4 bg-gray-50/60 dark:bg-muted/40 gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base sm:text-lg font-bold text-on-surface truncate">{docNumber}</span>
                {readOnly && (
                  <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/60 dark:text-amber-300">
                    Read Only
                  </span>
                )}
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase flex items-center gap-1 ${
                  isConfirmed
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300'
                    : 'bg-primary/10 text-primary'
                }`}>
                  {isConfirmed && <Lock className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />}
                  {isConfirmed ? 'Approved & Locked' : (document.status || 'draft').replace(/_/g, ' ')}
                </span>
              </div>
              <span className="text-xs text-muted-foreground truncate">Client: {client}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {onSubmitDraft && !isConfirmed && ['draft', 'proforma_draft'].includes(document.status) && (
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-xs"
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    await onSubmitDraft(document.id);
                    toast.success('Document submitted');
                    onClose();
                  } catch (e: any) {
                    toast.error(getApiErrorMessage(e, 'submit this document'));
                  } finally {
                    setIsSaving(false);
                  }
                }}
                loading={isSaving}
              >
                <Send className="h-4 w-4 mr-1.5" /> Submit for Approval
              </Button>
            )}

            {onEditDraft && !isConfirmed && (document.requestType === 'proforma' || document.documentType === 'proforma') && ['draft', 'proforma_draft', 'proforma_requested', 'proforma_rejected', 'revision_needed'].includes(document.status) && (
              <Button
                size="sm"
                variant="outline"
                className="text-indigo-700 border-indigo-300 hover:bg-indigo-50 font-semibold"
                onClick={() => {
                  onClose();
                  onEditDraft(document);
                }}
              >
                <Edit className="h-4 w-4 mr-1.5" /> Edit Proforma
              </Button>
            )}

            {onReviseQuotation && (document.requestType === 'quotation' || document.documentType === 'quotation') && !(document as any).proformaId && !hasRealProformaNumber(document as any) && !(document.status?.startsWith('proforma')) && !['sent', 'paid'].includes(document.status)
              && !isQuotationSuperseded({ supersededById: document.supersededById }) && isQuotationIssued({ status: document.status }) && (
              <Button
                size="sm"
                variant="outline"
                className="text-blue-800 border-blue-300 hover:bg-blue-50 font-semibold"
                onClick={() => {
                  onClose();
                  onReviseQuotation(document);
                }}
              >
                <Edit className="h-4 w-4 mr-1.5" /> {hasQuotationBeenEmailed({ emailSentAt: document.emailSentAt }) ? 'Revise Quotation' : 'Edit Quotation'}
              </Button>
            )}

            {onSendEmail && (document.requestType === 'quotation' || document.documentType === 'quotation')
              && isQuotationIssued({ status: document.status }) && !isQuotationSuperseded({ supersededById: document.supersededById }) && (
              <Button
                size="sm"
                variant="outline"
                className="text-primary border-primary/30 hover:bg-primary/5 font-semibold"
                onClick={() => {
                  onClose();
                  onSendEmail(document);
                }}
              >
                <Mail className="h-4 w-4 mr-1.5" /> Send Email
              </Button>
            )}

            {onRequestProforma && (document.requestType === 'quotation' || document.documentType === 'quotation') && isQuotationUsable({ status: document.status, supersededById: document.supersededById }) && !document.status?.startsWith('proforma') && !(document as any).proformaId && !['sent', 'paid'].includes(document.status) && (
              <Button
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-xs"
                onClick={() => {
                  onClose();
                  onRequestProforma(document);
                }}
              >
                <Send className="h-4 w-4 mr-1.5" /> Create Proforma
              </Button>
            )}

            {onSaveHtml && !isConfirmed && !readOnly && (
              <Button size="sm" variant="primary" onClick={handleSaveDraft} loading={isSaving}>
                <Save className="h-4 w-4 mr-1.5" /> Save to DB
              </Button>
            )}

            {onResubmit && !isConfirmed && (document.canResubmit || document.canResubmitProforma || (document.requestType === 'proforma' && document.status === 'proforma_rejected')) && (
              <Button size="sm" variant="outline" className="text-indigo-700 border-indigo-300 hover:bg-indigo-50 font-semibold" onClick={() => onOpenResubmissionForm?.(document)} loading={isSaving}>
                <Send className="h-4 w-4 mr-1.5" />
                {document.canResubmitProforma || document.requestType === 'proforma' ? 'Resubmit Proforma' : 'Resubmit Quotation'}
              </Button>
            )}

            {onApprove && !isConfirmed && document.canApproveNow && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" onClick={handleApproveAction} loading={isSaving}>
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
              </Button>
            )}

            {onReject && !isConfirmed && document.canRejectNow && (
              <Button size="sm" variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50 font-semibold" onClick={() => setRejectModalOpen(true)}>
                <XCircle className="h-4 w-4 mr-1.5" /> Return / Reject
              </Button>
            )}

            <Button size="sm" variant="secondary" onClick={handlePrintPdf}>
              <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
            </Button>


            <button
              onClick={onClose}
              className="rounded-lg p-2 text-on-surface-variant hover:bg-surface-variant transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-outline px-6 py-2 bg-surface gap-2">
          <button
            onClick={() => setActiveTab('editor')}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              activeTab === 'editor' ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-variant'
            }`}
          >
            <FileText className="h-4 w-4" /> PDF Document Preview
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              activeTab === 'history' ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-variant'
            }`}
          >
            <History className="h-4 w-4" /> Activity &amp; History ({document.history?.length || 0})
          </button>
        </div>

        {/* Reviewer note banner */}
        {document.reviewNote && ['revision_needed', 'rejected', 'proforma_rejected'].includes(document.status) && (
          <div className="border-b border-rose-200 bg-rose-50 px-6 py-3 flex items-start gap-3">
            <XCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-bold text-rose-800 mb-0.5">
                {document.status === 'proforma_rejected' ? 'Proforma Returned' : 'Document Returned'} — Reviewer Note
              </p>
              <p className="text-xs text-rose-700 leading-relaxed">{document.reviewNote}</p>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-hidden relative">
          {activeTab === 'editor' ? (
            <div className="h-full w-full relative bg-slate-100">
              {!html && (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  No document template available.
                </div>
              )}
              {html && (
                <>
                  {!iframeReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs">Loading document…</span>
                      </div>
                    </div>
                  )}
                  <iframe
                    ref={iframeRef}
                    title="PDF Document Preview"
                    srcDoc={isReadOnly ? lockHtmlTemplate(html) : html}
                    sandbox="allow-scripts allow-same-origin allow-modals allow-popups allow-forms"
                    className="w-full h-full border-0 bg-white"
                    onLoad={handleIframeLoad}
                  />
                </>
              )}
            </div>
          ) : (
            <div className="overflow-y-auto h-full p-6 space-y-6">
              {/* Revision History */}
              {document.documentRevisions && document.documentRevisions.length > 0 && (
                <div className="rounded-xl border border-outline bg-surface p-5 shadow-xs">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" /> Document Revisions
                  </h3>
                  <div className="space-y-3">
                    {document.documentRevisions.map((rev) => (
                      <div key={rev.revisionNumber} className="flex items-start justify-between rounded-lg border border-outline/60 p-3 text-xs bg-surface-variant/30">
                        <div>
                          <span className="font-bold text-primary mr-2">Revision v{rev.revisionNumber}</span>
                          <span className="text-on-surface-variant">by {rev.createdByName || 'User'}</span>
                          <p className="text-muted-foreground mt-1">{rev.note || 'No revision notes'}</p>
                          {rev.rejectionNote && (
                            <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-[10px]">
                              <span className="font-semibold text-rose-800">Rejection Reason:</span>
                              <span className="text-rose-700 ml-1">{rev.rejectionNote}</span>
                            </div>
                          )}
                          {rev.rejectedBy && (
                            <div className="mt-1 text-[10px] text-rose-600">
                              Rejected by: <span className="font-medium">{rev.rejectedBy}</span>
                              {rev.rejectedAt && <span className="ml-1">at {new Date(rev.rejectedAt).toLocaleString()}</span>}
                            </div>
                          )}
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          {rev.createdAt ? new Date(rev.createdAt).toLocaleString() : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Audit Trail */}
              <div className="rounded-xl border border-outline bg-surface p-5 shadow-xs">
                <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                  <History className="h-4 w-4 text-primary" /> Approval Audit Trail
                </h3>
                {document.history && document.history.length > 0 ? (
                  <div className="space-y-3">
                    {document.history.map((h, i) => (
                      <div key={i} className="flex items-start justify-between rounded-lg border border-outline/60 p-3 text-xs">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-on-surface">{h.byName || 'System'}</span>
                            <span className="text-muted-foreground">changed status to</span>
                            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary uppercase">
                              {h.to?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          {h.note && <p className="text-muted-foreground mt-1.5 italic">"{h.note}"</p>}
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          {h.at ? new Date(h.at).toLocaleString() : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No history logged yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      <Modal open={rejectModalOpen} onClose={() => setRejectModalOpen(false)} title="Return Document to Sales">
        <div className="space-y-4 p-4">
          <p className="text-xs text-muted-foreground">
            Provide feedback explaining what changes Sales needs to make before resubmitting.
          </p>
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="e.g. Please correct the GST calculation and update delivery terms..."
            className="w-full h-28 rounded-lg border border-outline p-3 text-xs focus:outline-hidden focus:ring-2 focus:ring-primary"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleRejectConfirm} loading={isSaving}>
              Confirm Return
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
